import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Divider, Dropdown, Input, Loader, Message, Panel, Stack, Tooltip, Whisper, toaster } from 'rsuite';
import { Button as NsiButton, Modal, sortRows, useLastPresent, useTableSort } from '@nsi-advisory-services/components';
import L from 'leaflet';

import {
  useArchivedProjects,
  useCreatePursuitConnectProject,
  useHasConnectSuite,
  useHasPursuitConnect,
  useMyProjectsViewPreference,
  useUpdateMyProjectsViewPreference,
  useProjects,
  usePursuitConnectPursuits,
  useUnarchiveProject,
  useArchiveProject,
  useDeleteProject,
  useMyHiddenProjects,
  useSetProjectHiddenForMe,
  useUsers,
} from '../hooks/useQueries';
import { ensureLeafletDefaultIcon } from '../utils/leafletIcons';
import useAuthSession from '../auth/useAuthSession';
import ContextMenu, { contextMenuPointFor } from '../components/ContextMenu';
import usePermissions, { canCreateProjects } from '../hooks/usePermissions';

import './ConfigurationSheet.css';
import './HomePage.css';

ensureLeafletDefaultIcon();

const DEFAULT_MAP_CENTER = [37, -96];
const DEFAULT_MAP_ZOOM = 2;
const SINGLE_POINT_ZOOM = 10;
const COORD_GROUP_PRECISION = 6;
const US_BOUNDS = L.latLngBounds(
  [24.396308, -124.848974],
  [49.384358, -66.885444]
);

const parseCoordinate = (raw) => {
  if (raw === null || raw === undefined || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

const hasNonZeroCoordinates = (lat, lng) => {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  return !(Math.abs(lat) < 0.000001 && Math.abs(lng) < 0.000001);
};

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const formatCurrency = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return currencyFormatter.format(n);
};

const plural = (value, unit) => `${value} ${unit}${value === 1 ? '' : 's'}`;

const formatMonthsAsYearsAndMonths = (totalMonths) => {
  const monthsRaw = Number(totalMonths);
  if (!Number.isFinite(monthsRaw)) return '—';

  const monthsInt = Math.max(0, Math.floor(monthsRaw));
  const years = Math.floor(monthsInt / 12);
  const months = monthsInt % 12;

  if (years > 0) {
    return months > 0 ? `${plural(years, 'year')} ${plural(months, 'month')}` : plural(years, 'year');
  }
  return plural(months, 'month');
};

const parseDateParts = (iso) => {
  const s = typeof iso === 'string' ? iso.slice(0, 10) : '';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;
  return { year, month, day };
};

const dayNumber = (parts) => {
  if (!parts) return null;
  return Math.floor(Date.UTC(parts.year, parts.month - 1, parts.day) / 86400000);
};

const monthDiffRoundedDown = (fromParts, toParts) => {
  const fromMonth = fromParts.year * 12 + (fromParts.month - 1);
  const toMonth = toParts.year * 12 + (toParts.month - 1);
  let months = toMonth - fromMonth;
  if (toParts.day < fromParts.day) months -= 1;
  return months;
};

// Ended projects always sit below the rest, in both the list and the cards, whatever the sort; each
// group keeps the order it already had. "Ended" is what the status chip shows: past its end date and
// not a pursuit (a pursuit shows "Pursuit" instead).
const endedLast = (items, isEnded) => [...items.filter((x) => !isEnded(x)), ...items.filter(isEnded)];

// The first sort key of all: projects the user is on come before the rest, each group in the
// order it already had.
const mineFirst = (items, isMine) => [...items.filter(isMine), ...items.filter((x) => !isMine(x))];

const formatRemainingTime = (startIso, endIso) => {
  const endParts = parseDateParts(endIso);
  if (!endParts) return 'No end date';

  const startParts = parseDateParts(startIso);
  const now = new Date();
  const todayParts = { year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate() };

  const endDay = dayNumber(endParts);
  const todayDay = dayNumber(todayParts);
  if (endDay === null || todayDay === null) return '—';
  if (endDay < todayDay) return 'Ended';
  if (endDay === todayDay) return 'Ends today';

  if (startParts) {
    const startDay = dayNumber(startParts);
    if (startDay !== null && startDay > todayDay) {
      const monthsUntilStart = monthDiffRoundedDown(todayParts, startParts);
      if (monthsUntilStart >= 1) return `Starts in ${formatMonthsAsYearsAndMonths(monthsUntilStart)}`;
      return `Starts in ${plural(Math.max(1, startDay - todayDay), 'day')}`;
    }
  }

  const monthsRemaining = monthDiffRoundedDown(todayParts, endParts);
  if (monthsRemaining >= 1) return `${formatMonthsAsYearsAndMonths(monthsRemaining)} remaining`;
  return `${plural(Math.max(1, endDay - todayDay), 'day')} remaining`;
};

const formatMonthYear = (iso) => {
  const s = typeof iso === 'string' ? iso.slice(0, 10) : null;
  if (!s) return null;
  const m = /^([0-9]{4})-([0-9]{2})-([0-9]{2})$/.exec(s);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;
  const d = new Date(year, month - 1, day);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short' });
};

const initialsForName = (raw) => {
  const s = String(raw || '').trim();
  if (!s) return '?';
  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[parts.length - 1][0] || ''}`.toUpperCase();
};

// A project card's name, at the largest size between these that fits on its two lines. Measured, not
// guessed from the length, so it uses the room there actually is (narrower beside a logo, wider
// without). A name that won't fit even at the minimum keeps the minimum and ends in an ellipsis.
const CARD_TITLE_MAX_PX = 28;
const CARD_TITLE_MIN_PX = 14;

function CardTitle({ text }) {
  const ref = useRef(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    // The title is clamped to two lines, so anything taller (or a single word wider) than the box
    // means it doesn't fit at this size.
    const fits = () => el.scrollHeight <= el.clientHeight + 1 && el.scrollWidth <= el.clientWidth + 1;
    const fit = () => {
      let lo = CARD_TITLE_MIN_PX;
      let hi = CARD_TITLE_MAX_PX;
      let best = CARD_TITLE_MIN_PX;
      while (lo <= hi) {
        const mid = Math.floor((lo + hi) / 2);
        el.style.fontSize = `${mid}px`;
        if (fits()) {
          best = mid;
          lo = mid + 1;
        } else {
          hi = mid - 1;
        }
      }
      el.style.fontSize = `${best}px`;
    };

    fit();

    // Refit when the card's width changes, and once web fonts load (they change the text's width).
    // Height changes are our own doing, so they are ignored. The refit waits for the next frame:
    // resizing the text inside the observer's callback, while the sidebar animates every card's
    // width, is what makes the browser report "ResizeObserver loop completed with undelivered
    // notifications".
    let width = el.clientWidth;
    let frame = 0;
    const observer = typeof ResizeObserver === 'function'
      ? new ResizeObserver(() => {
        if (el.clientWidth === width) return;
        width = el.clientWidth;
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(fit);
      })
      : null;
    observer?.observe(el);
    let cancelled = false;
    document.fonts?.ready?.then(() => {
      if (!cancelled) fit();
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
      observer?.disconnect();
    };
  }, [text]);

  return (
    <div ref={ref} className="ppc-project-card-title" title={text}>
      {text}
    </div>
  );
}

// Each entry has a light and a dark pair. Light is a pastel fill with dark text; dark is a
// deep fill of the same hue with light text, so initials stay readable on a dark card.
const AVATAR_PALETTE = [
  { background: '#f8d7da', color: '#7f1d2d', darkBackground: '#4a1d24', darkColor: '#f8b4bd' },
  { background: '#fde2b8', color: '#7a3a00', darkBackground: '#4a2c0f', darkColor: '#fbc98b' },
  { background: '#fff3bf', color: '#775d00', darkBackground: '#423a10', darkColor: '#f5dd7a' },
  { background: '#daf5d2', color: '#1f5d2f', darkBackground: '#1c3a24', darkColor: '#a7e3b0' },
  { background: '#d9f2f0', color: '#145a57', darkBackground: '#123a38', darkColor: '#9ee0da' },
  { background: '#dbeafe', color: '#1e3a8a', darkBackground: '#1c2c4f', darkColor: '#a9c8fb' },
  { background: '#e9ddff', color: '#4b2f77', darkBackground: '#2f2450', darkColor: '#cdb8fb' },
  { background: '#f7d9ef', color: '#7c275e', darkBackground: '#45203a', darkColor: '#f0b3dd' },
  { background: '#fce1f1', color: '#7f1d4d', darkBackground: '#4a1c33', darkColor: '#f6b2d3' },
  { background: '#e5e7eb', color: '#374151', darkBackground: '#2c3138', darkColor: '#cbd2da' },
];

const hashText = (value) => {
  const s = String(value || '');
  let hash = 0;
  for (let i = 0; i < s.length; i += 1) {
    hash = (hash * 31 + s.charCodeAt(i)) >>> 0;
  }
  return hash;
};

// Colors go in as custom properties rather than background/color, so the stylesheet can
// pick the light or dark pair (an inline background could not be overridden by the theme).
const avatarStyleForName = (name) => {
  const paletteItem = AVATAR_PALETTE[hashText(name) % AVATAR_PALETTE.length];
  return {
    '--avatar-bg': paletteItem.background,
    '--avatar-fg': paletteItem.color,
    '--avatar-bg-dark': paletteItem.darkBackground,
    '--avatar-fg-dark': paletteItem.darkColor,
  };
};

const normalizeLooseName = (value) => {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
};

const formatWinLikelihood = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  if (n > 0 && n <= 1) return `${Math.round(n * 100)}%`;
  return `${Math.round(n)}%`;
};

const isPursuitProject = (project) => Boolean(project?.isPursuit ?? project?.is_pursuit);

const PROJECT_VIEW_STORAGE_PREFIX = 'ppc.projects.view';
const VALID_PROJECT_VIEWS = new Set(['grid', 'list']);

const projectViewStorageKey = (userId) => `${PROJECT_VIEW_STORAGE_PREFIX}.${userId || 'anonymous'}`;

// Server-side (per user, cross-device) is authoritative; localStorage is only a
// fast cache so the right view paints on load rather than flashing the default.
const readStoredProjectView = (userId) => {
  try {
    const v = window.localStorage.getItem(projectViewStorageKey(userId));
    return VALID_PROJECT_VIEWS.has(v) ? v : null;
  } catch {
    return null;
  }
};

const writeStoredProjectView = (userId, view) => {
  try {
    window.localStorage.setItem(projectViewStorageKey(userId), view);
  } catch {
    // private mode or quota — the server copy still holds the choice
  }
};

const initialForName = (raw) => {
  const s = String(raw ?? '').trim();
  return s ? s[0].toUpperCase() : '?';
};

export default function ProjectsPage() {
  const navigate = useNavigate();
  const permissions = usePermissions();
  const [searchParams] = useSearchParams();
  const [projectSearch, setProjectSearch] = useState('');
  const [pursuitSearch, setPursuitSearch] = useState('');
  const [showPursuits, setShowPursuits] = useState(true);
  const { userId, isSignedIn } = useAuthSession();
  const [projectView, setProjectView] = useState(() => readStoredProjectView(userId) || 'grid');

  const { data: viewPref } = useMyProjectsViewPreference(userId, { enabled: Boolean(isSignedIn) });
  const updateProjectsView = useUpdateMyProjectsViewPreference(userId);

  // Repaint from the cache as soon as the signed-in user resolves.
  useEffect(() => {
    const stored = readStoredProjectView(userId);
    if (stored && stored !== projectView) setProjectView(stored);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Reconcile with the server once it loads. A saved value wins — that is how a
  // choice made on another device reaches this one. Null means "never set", so
  // whatever is local stays.
  useEffect(() => {
    const serverView = viewPref?.projectsView;
    if (VALID_PROJECT_VIEWS.has(serverView) && serverView !== projectView) {
      setProjectView(serverView);
      writeStoredProjectView(userId, serverView);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewPref?.projectsView]);

  const chooseProjectView = (next) => {
    if (!VALID_PROJECT_VIEWS.has(next)) return;
    setProjectView(next);
    writeStoredProjectView(userId, next);
    if (isSignedIn) {
      // If this fails (offline, migration not run) localStorage still holds the
      // choice on this device.
      updateProjectsView.mutate(next);
    }
  };
  const [archivedExpanded, setArchivedExpanded] = useState(false);
  const [showPullPursuitsModal, setShowPullPursuitsModal] = useState(false);
  const [activeImportKey, setActiveImportKey] = useState('');
  const [importedPursuitKeys, setImportedPursuitKeys] = useState({});
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markersLayerRef = useRef(null);

  const hasConnectSuite = useHasConnectSuite();
  // Pulling pursuits needs both the suite and a configured PursuitConnect URL.
  const canPullPursuits = useHasPursuitConnect() && hasConnectSuite && canCreateProjects(permissions);

  // Back-compat: old deep links were "/?projectId=...&taskOrderId=...".
  useEffect(() => {
    const rawProjectId = searchParams?.get?.('projectId');
    const rawTaskOrderId = searchParams?.get?.('taskOrderId');
    const pid = rawProjectId ? Number(rawProjectId) : null;
    const toid = rawTaskOrderId ? Number(rawTaskOrderId) : null;

    if (!Number.isInteger(pid) || pid <= 0) return;

    if (Number.isInteger(toid) && toid > 0) {
      navigate(`/projects/${pid}/task-orders/${toid}`, { replace: true });
      return;
    }

    navigate(`/projects/${pid}`, { replace: true });
  }, [searchParams, navigate]);

  const {
    data: projects = [],
    isLoading: projectsLoading,
    error: projectsError,
  } = useProjects();

  const {
    data: pursuitRows = [],
    isLoading: pursuitsLoading,
    error: pursuitsError,
    isFetching: pursuitsFetching,
    refetch: refetchPursuits,
  } = usePursuitConnectPursuits({
    enabled: canPullPursuits && showPullPursuitsModal,
  });

  const {
    data: usersForPursuits = [],
  } = useUsers({
    enabled: canPullPursuits && showPullPursuitsModal,
  });

  const createPursuitConnectProject = useCreatePursuitConnectProject();

  const normalizedProjectSearch = useMemo(() => String(projectSearch || '').trim().toLowerCase(), [projectSearch]);
  const normalizedPursuitSearch = useMemo(() => String(pursuitSearch || '').trim().toLowerCase(), [pursuitSearch]);

  const projectNamesSet = useMemo(() => {
    const set = new Set();
    for (const p of Array.isArray(projects) ? projects : []) {
      const key = normalizeLooseName(p?.name);
      if (key) set.add(key);
    }
    return set;
  }, [projects]);

  const usersByNormalizedName = useMemo(() => {
    const map = new Map();
    const rows = Array.isArray(usersForPursuits) ? usersForPursuits : [];
    for (const u of rows) {
      const key = normalizeLooseName(u?.name);
      if (!key || map.has(key)) continue;
      const id = Number(u?.id);
      if (!Number.isInteger(id)) continue;
      map.set(key, { id, name: String(u?.name || '') });
    }
    return map;
  }, [usersForPursuits]);

  const importablePursuits = useMemo(() => {
    const rows = Array.isArray(pursuitRows) ? pursuitRows : [];
    return rows.map((row, idx) => {
      const name = String(row?.name || '').trim();
      const key = normalizeLooseName(name) || `row-${idx}`;
      const ownerLabel = String(row?.pursuitOwner || '').trim();
      const ownerKey = normalizeLooseName(ownerLabel);
      const suggestedPic = usersByNormalizedName.get(ownerKey);
      const upstreamId = row?.id ?? row?.pursuitId ?? row?.dealId ?? row?.opportunityId ?? null;
      const rowKey = upstreamId !== null && upstreamId !== undefined && String(upstreamId).trim()
        ? `pursuit-${String(upstreamId).trim()}`
        : `pursuit-${key}-${idx}`;
      const alreadyExists = key && projectNamesSet.has(key);
      const alreadyImported = Boolean(importedPursuitKeys[key]);
      // A project here already linked to this pursuit (it was imported, or linked later).
      const alreadyLinked = row?.linkedProjectId !== null && row?.linkedProjectId !== undefined;
      return {
        rowKey,
        key,
        pursuitId: Number.isInteger(row?.id) ? row.id : null,
        name,
        estimatedStartDate: row?.estimatedStartDate || null,
        totalBudget: row?.totalBudget,
        pursuitOwner: ownerLabel || null,
        pursuitOwnerKey: ownerKey,
        winLikelihood: row?.winLikelihood,
        suggestedPic,
        alreadyExists,
        alreadyImported,
        alreadyLinked,
      };
    });
  }, [pursuitRows, usersByNormalizedName, projectNamesSet, importedPursuitKeys]);

  const filteredImportablePursuits = useMemo(() => {
    return importablePursuits.filter((row) => {
      if (!normalizedPursuitSearch) return true;

      const name = String(row?.name || '').toLowerCase();
      const owner = String(row?.pursuitOwner || '').toLowerCase();
      return name.includes(normalizedPursuitSearch) || owner.includes(normalizedPursuitSearch);
    });
  }, [importablePursuits, normalizedPursuitSearch]);

  useEffect(() => {
    if (showPullPursuitsModal) return;
    setPursuitSearch('');
  }, [showPullPursuitsModal]);

  const onImportPursuit = useCallback(async (row) => {
    const rowKey = String(row?.rowKey || row?.key || '');
    const key = String(row?.key || rowKey);
    const title = String(row?.name || '').trim();
    if (!title) return;

    setActiveImportKey(rowKey);
    try {
      const created = await createPursuitConnectProject.mutateAsync({
        // Keeps the project linked to its pursuit, here and in PursuitConnect.
        pursuitId: row?.pursuitId ?? null,
        title,
        startDate: row?.estimatedStartDate || null,
        budget: row?.totalBudget ?? null,
        picUserId: Number.isInteger(row?.suggestedPic?.id) ? Number(row.suggestedPic.id) : null,
        isPursuit: true,
      });

      setImportedPursuitKeys((prev) => ({ ...prev, [key]: true }));
      toaster.push(
        <Message type="success" showIcon>
          Imported "{title}" as a project.
        </Message>,
        { placement: 'topEnd', duration: 2600 }
      );
      // The project exists either way; the link can be made later from its page.
      if (created?.pursuitLinkError) {
        toaster.push(
          <Message type="warning" showIcon closable>
            {String(created.pursuitLinkError)}
          </Message>,
          { placement: 'topEnd', duration: 6000 }
        );
      }
    } catch (err) {
      toaster.push(
        <Message type="error" showIcon>
          {String(err?.message || 'Failed to import pursuit.')}
        </Message>,
        { placement: 'topEnd', duration: 4200 }
      );
    } finally {
      setActiveImportKey('');
    }
  }, [createPursuitConnectProject]);

  const hasAnyPursuit = useMemo(
    () => (Array.isArray(projects) ? projects : []).some(isPursuitProject),
    [projects]
  );

  // "Hide for me": projects this user has tucked away. They leave the cards, list and map and sit in
  // the collapsed Hidden section instead; nobody else's view changes.
  const { data: hiddenProjectsData } = useMyHiddenProjects(userId, { enabled: Boolean(isSignedIn) });
  const hidingEnabled = hiddenProjectsData?.enabled === true;
  const hiddenProjectIds = useMemo(
    () => new Set((hiddenProjectsData?.projectIds || []).map(Number)),
    [hiddenProjectsData?.projectIds]
  );
  const setProjectHidden = useSetProjectHiddenForMe(userId, {
    onError: (err) => {
      toaster.push(
        <Message showIcon type="error" closable>
          {String(err?.message || 'Could not update hidden projects.')}
        </Message>,
        { placement: 'topEnd' }
      );
    },
  });
  const [hiddenExpanded, setHiddenExpanded] = useState(false);

  const hiddenProjects = useMemo(
    () => (Array.isArray(projects) ? projects : [])
      .filter((p) => hiddenProjectIds.has(Number(p?.id)))
      .sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || ''))),
    [hiddenProjectIds, projects]
  );

  const visibleProjects = useMemo(() => {
    const source = (Array.isArray(projects) ? projects : []).filter((p) => !hiddenProjectIds.has(Number(p?.id)));
    if (showPursuits) return source;
    return source.filter((p) => !isPursuitProject(p));
  }, [hiddenProjectIds, projects, showPursuits]);

  const filteredProjects = useMemo(() => {
    const source = Array.isArray(visibleProjects) ? visibleProjects : [];
    if (!normalizedProjectSearch) return source;

    return source.filter((p) => {
      const name = String(p?.name || '').toLowerCase();
      const city = String(p?.locationCity ?? p?.location_city ?? '').toLowerCase();
      const assignedPeopleNames = Array.isArray(p?.assignedPeopleNames) ? p.assignedPeopleNames : [];
      const people = assignedPeopleNames.join(' ').toLowerCase();
      return name.includes(normalizedProjectSearch)
        || city.includes(normalizedProjectSearch)
        || people.includes(normalizedProjectSearch);
    });
  }, [visibleProjects, normalizedProjectSearch]);

  const {
    data: archivedProjectsData = [],
    error: archivedProjectsError,
  } = useArchivedProjects();

  const archivedProjects = useMemo(() => {
    const rows = Array.isArray(archivedProjectsData) ? archivedProjectsData : [];
    return rows.slice().sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || '')));
  }, [archivedProjectsData]);

  const unarchiveProject = useUnarchiveProject({
    onSuccess: async () => {
      toaster.push(
        <Message showIcon type="success" closable>
          Project moved back to active projects.
        </Message>,
        { placement: 'topEnd' }
      );
    },
  });

  // Archived projects can go straight to the trash (soft delete, 30-day
  // retention) without being restored first.
  const [pendingTrashProject, setPendingTrashProject] = useState(null);
  const pendingTrashProjectShown = useLastPresent(pendingTrashProject); // keeps the dialog steady while it fades out
  const trashProject = useDeleteProject();

  const submitTrashProject = async () => {
    const id = Number(pendingTrashProject?.id);
    if (!Number.isInteger(id)) return;
    try {
      await trashProject.mutateAsync(id);
      setPendingTrashProject(null);
      toaster.push(
        <Message showIcon type="success" closable>
          Project moved to trash.
        </Message>,
        { placement: 'topEnd' }
      );
    } catch (err) {
      toaster.push(
        <Message showIcon type="error" closable>
          {String(err?.message || 'Failed to move project to trash.')}
        </Message>,
        { placement: 'topEnd' }
      );
    }
  };

  const listSort = useTableSort('name');

  const listRows = useMemo(
    () => (filteredProjects || []).map((p) => {
      const startDate = p?.startDate ?? p?.start_date;
      const endDate = p?.endDate ?? p?.end_date;
      const locationCity = String(p?.locationCity ?? p?.location_city ?? '').trim();
      const lat = parseCoordinate(p?.locationLat ?? p?.location_lat);
      const lng = parseCoordinate(p?.locationLng ?? p?.location_lng);
      const hasCoords = hasNonZeroCoordinates(lat, lng);
      const rangeStart = formatMonthYear(startDate);
      const rangeEnd = formatMonthYear(endDate);

      return {
        pid: Number(p?.id),
        name: String(p?.name || 'Untitled Project'),
        photoUrl: p?.photoUrl || null,
        pursuit: isPursuitProject(p),
        startDate: String(startDate ?? ''),
        dateRange: rangeStart && rangeEnd ? `${rangeStart} - ${rangeEnd}` : '—',
        locationLabel: locationCity || (hasCoords ? `${lat.toFixed(3)}, ${lng.toFixed(3)}` : 'Not set'),
        peopleCount: Number(p?.assignedPeopleCount ?? p?.assigned_people_count ?? 0),
        remainingTime: formatRemainingTime(startDate, endDate),
      };
    }),
    [filteredProjects]
  );

  const sortedListRows = useMemo(
    () => mineFirst(
      endedLast(
        sortRows(listRows, listSort.sortKey, listSort.sortDir, {
          name: (r) => r.name,
          startDate: (r) => r.startDate,
          location: (r) => (r.locationLabel === 'Not set' ? '' : r.locationLabel),
          people: (r) => r.peopleCount,
          status: (r) => (r.pursuit ? 'Pursuit' : String(r.remainingTime ?? '')),
        }),
        (r) => !r.pursuit && r.remainingTime === 'Ended'
      ),
      (r) => permissions.isMember(r.pid)
    ),
    [listRows, listSort.sortKey, listSort.sortDir, permissions]
  );

  const gridProjects = useMemo(
    () => mineFirst(
      endedLast(
        filteredProjects || [],
        (p) => !isPursuitProject(p) && formatRemainingTime(p?.startDate ?? p?.start_date, p?.endDate ?? p?.end_date) === 'Ended'
      ),
      (p) => permissions.isMember(p?.id)
    ),
    [filteredProjects, permissions]
  );

  const openProject = useCallback((pid) => {
    if (!Number.isInteger(pid) || pid <= 0) return;
    navigate(`/projects/${pid}`);
  }, [navigate]);

  // Right-click menu on a project row or card. Archive asks first, as it does on the project page.
  const [projectMenu, setProjectMenu] = useState(null);
  const closeProjectMenu = useCallback(() => setProjectMenu(null), []);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const archiveTargetShown = useLastPresent(archiveTarget); // keeps the dialog steady while it fades out
  const archiveProject = useArchiveProject();

  const hideProjectForMe = (pid, name) => {
    setProjectHidden.mutate({ projectId: pid, hidden: true });
    let toastKey = null;
    toastKey = toaster.push(
      <Message showIcon type="info" closable>
        <strong>{name || 'Project'}</strong> is hidden for you. Find it under Hidden, above the map.{' '}
        <Button
          appearance="link"
          size="xs"
          style={{ padding: 0, verticalAlign: 'baseline' }}
          onClick={() => {
            setProjectHidden.mutate({ projectId: pid, hidden: false });
            if (toastKey) toaster.remove(toastKey);
          }}
        >
          Undo
        </Button>
      </Message>,
      { placement: 'topEnd', duration: 6000 }
    );
  };

  // `placement` overrides where it opens: a point for the keyboard, or { x, y, anchor, align } for
  // the card's menu button.
  const openProjectMenu = (e, pid, name, placement = null) => {
    if (!Number.isInteger(pid) || pid <= 0) return;
    e.preventDefault();
    const isHidden = hiddenProjectIds.has(pid);
    setProjectMenu({
      x: e.clientX,
      y: e.clientY,
      ...placement,
      items: [
        { key: 'view', label: 'View project', icon: 'bi-box-arrow-up-right', onSelect: () => openProject(pid) },
        { key: 'charts', label: 'View charts', icon: 'bi-graph-up', onSelect: () => navigate(`/charts?projectId=${pid}&taskOrderId=current`) },
        { key: 'gantt', label: 'View Gantt', icon: 'bi-bar-chart-steps', onSelect: () => navigate(`/projects/${pid}/gantt`) },
        { key: 'actuals', label: 'View actuals', icon: 'bi-clock-history', onSelect: () => navigate(`/projects/${pid}/actuals`) },
        ...(hidingEnabled
          ? [
            isHidden
              ? {
                key: 'unhide',
                label: 'Unhide for me',
                icon: 'bi-eye',
                onSelect: () => setProjectHidden.mutate({ projectId: pid, hidden: false }),
              }
              : { key: 'hide', label: 'Hide for me', icon: 'bi-eye-slash', onSelect: () => hideProjectForMe(pid, name) },
          ]
          : []),
        ...(permissions.canProject(pid, 'manage')
          ? [
            { key: 'divider', divider: true },
            {
              key: 'archive',
              label: 'Archive project',
              icon: 'bi-archive',
              danger: true,
              onSelect: () => {
                archiveProject.reset();
                setArchiveTarget({ id: pid, name });
              },
            },
          ]
          : []),
      ],
    });
  };

  // The Menu key or Shift+F10 opens the same menu from the keyboard.
  const isContextMenuKey = (e) => e.key === 'ContextMenu' || (e.shiftKey && e.key === 'F10');

  const submitArchiveProject = async () => {
    const id = Number(archiveTarget?.id);
    if (!Number.isInteger(id)) return;
    try {
      await archiveProject.mutateAsync(id);
      setArchiveTarget(null);
      toaster.push(
        <Message showIcon type="success" closable>
          Project archived. Restore it from Archived Projects below.
        </Message>,
        { placement: 'topEnd' }
      );
    } catch {
      // The mutation's error is shown in the dialog.
    }
  };

  const projectsWithLocation = useMemo(() => {
    return (filteredProjects || [])
      .map((p) => {
        const lat = parseCoordinate(p?.locationLat ?? p?.location_lat);
        const lng = parseCoordinate(p?.locationLng ?? p?.location_lng);
        if (!hasNonZeroCoordinates(lat, lng)) return null;
        return {
          id: Number(p?.id),
          name: String(p?.name || 'Untitled Project'),
          city: String(p?.locationCity ?? p?.location_city ?? '').trim() || null,
          lat,
          lng,
        };
      })
      .filter(Boolean);
  }, [filteredProjects]);

  const mapCenter = useMemo(() => {
    if (projectsWithLocation.length === 0) return DEFAULT_MAP_CENTER;
    const sum = projectsWithLocation.reduce(
      (acc, p) => {
        acc.lat += Number(p.lat);
        acc.lng += Number(p.lng);
        return acc;
      },
      { lat: 0, lng: 0 }
    );
    return [sum.lat / projectsWithLocation.length, sum.lng / projectsWithLocation.length];
  }, [projectsWithLocation]);

  const locationMarkerGroups = useMemo(() => {
    const groups = new Map();

    projectsWithLocation.forEach((p) => {
      const lat = Number(p.lat);
      const lng = Number(p.lng);
      const key = `${lat.toFixed(COORD_GROUP_PRECISION)}:${lng.toFixed(COORD_GROUP_PRECISION)}`;

      if (!groups.has(key)) {
        groups.set(key, {
          lat,
          lng,
          projects: [],
        });
      }

      groups.get(key).projects.push(p);
    });

    return Array.from(groups.values());
  }, [projectsWithLocation]);

  useEffect(() => {
    if (!mapContainerRef.current) return;
    if (mapRef.current) return;

    const map = L.map(mapContainerRef.current, {
      zoomControl: true,
      scrollWheelZoom: true,
      zoomAnimation: false,
      fadeAnimation: false,
      markerZoomAnimation: false,
    }).setView(mapCenter, DEFAULT_MAP_ZOOM, { animate: false });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      referrerPolicy: 'strict-origin-when-cross-origin',
    }).addTo(map);

    markersLayerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;

    return () => {
      map.stop();
      map.off();
      if (markersLayerRef.current) {
        markersLayerRef.current.clearLayers();
      }
      map.remove();
      if (mapRef.current === map) mapRef.current = null;
      markersLayerRef.current = null;
    };
  }, [mapCenter]);

  useEffect(() => {
    const map = mapRef.current;
    const markersLayer = markersLayerRef.current;
    if (!map || !markersLayer) return;

    markersLayer.clearLayers();

    if (locationMarkerGroups.length === 0) {
      map.fitBounds(US_BOUNDS, {
        padding: [20, 20],
        maxZoom: DEFAULT_MAP_ZOOM,
        animate: false,
      });
      map.invalidateSize();
      return;
    }

    const points = [];
    locationMarkerGroups.forEach((group) => {
      const point = [Number(group.lat), Number(group.lng)];
      points.push(point);

      const sortedProjects = [...(group.projects || [])].sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || '')));
      const title = sortedProjects.length > 1
        ? `${sortedProjects.length} Projects`
        : String(sortedProjects[0]?.name || 'Untitled Project');
      const subtitleSource = sortedProjects.find((p) => String(p?.city || '').trim())?.city || `${group.lat.toFixed(4)}, ${group.lng.toFixed(4)}`;

      const popupRoot = document.createElement('div');
      popupRoot.style.minWidth = '220px';

      const headerEl = document.createElement('div');
      headerEl.style.fontWeight = '700';
      headerEl.textContent = title;
      popupRoot.appendChild(headerEl);

      const subtitleEl = document.createElement('div');
      subtitleEl.style.fontSize = '12px';
      subtitleEl.style.color = '#555';
      subtitleEl.style.marginBottom = '6px';
      subtitleEl.textContent = subtitleSource;
      popupRoot.appendChild(subtitleEl);

      sortedProjects.forEach((project) => {
        const projectButton = document.createElement('button');
        projectButton.type = 'button';
        projectButton.style.display = 'block';
        projectButton.style.width = '100%';
        projectButton.style.textAlign = 'left';
        projectButton.style.padding = '0';
        projectButton.style.margin = '0 0 4px 0';
        projectButton.style.border = 'none';
        projectButton.style.background = 'transparent';
        projectButton.style.cursor = 'pointer';
        projectButton.style.fontSize = '12px';
        projectButton.style.lineHeight = '1.4';
        projectButton.style.color = '#0a5cb8';
        projectButton.textContent = `• ${String(project?.name || 'Untitled Project')}`;

        projectButton.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          const projectId = Number(project?.id);
          if (!Number.isInteger(projectId) || projectId <= 0) return;
          openProject(projectId);
        });

        popupRoot.appendChild(projectButton);
      });

      L.marker(point).bindPopup(popupRoot).addTo(markersLayer);
    });

    if (points.length === 1) {
      map.setView(points[0], SINGLE_POINT_ZOOM, { animate: false });
    } else {
      map.fitBounds(points, { padding: [28, 28], animate: false });
    }

    map.invalidateSize();
  }, [locationMarkerGroups, openProject]);

  return (
    <div className="ppc-page">
      <div className="ppc-projects-header-row">
        <div className="ppc-projects-header-lead">
          <h3 style={{ margin: 0 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <i className="bi bi-collection" aria-hidden="true" />
              Projects
            </span>
          </h3>

          <div className="ppc-projects-segment" role="group" aria-label="Project view">
            <button
              type="button"
              className={`ppc-projects-btn ppc-projects-btn--icon${projectView === 'grid' ? ' ppc-projects-btn--primary' : ''}`}
              onClick={() => chooseProjectView('grid')}
              aria-pressed={projectView === 'grid'}
              aria-label="Card view"
              title="Card view"
            >
              <i className="bi bi-grid-3x3-gap-fill" aria-hidden="true" />
            </button>
            <button
              type="button"
              className={`ppc-projects-btn ppc-projects-btn--icon${projectView === 'list' ? ' ppc-projects-btn--primary' : ''}`}
              onClick={() => chooseProjectView('list')}
              aria-pressed={projectView === 'list'}
              aria-label="List view"
              title="List view"
            >
              <i className="bi bi-list-ul" aria-hidden="true" />
            </button>
          </div>
        </div>
        <div className="ppc-projects-header-search-wrap">
          <Input
            value={projectSearch}
            onChange={setProjectSearch}
            placeholder="Search projects, city, or people"
            size="md"
          />
        </div>
        <div className="ppc-projects-header-actions">
          <div className="ppc-projects-segment" role="group" aria-label="Pursuit controls">
            {canPullPursuits ? (
              <button
                type="button"
                className="ppc-projects-btn"
                onClick={() => {
                  setShowPullPursuitsModal(true);
                  void refetchPursuits();
                }}
                aria-label="Pull pursuits"
                title="Pull pursuits"
              >
                Pull Pursuits
                <i className="bi bi-cloud-arrow-down" aria-hidden="true" />
              </button>
            ) : null}

            {hasAnyPursuit ? (
              <button
                type="button"
                className={`ppc-projects-btn${showPursuits ? ' ppc-projects-btn--primary' : ''}`}
                onClick={() => setShowPursuits((prev) => !prev)}
                aria-label={showPursuits ? 'Hide pursuits' : 'Show pursuits'}
                title={showPursuits ? 'Hide pursuits' : 'Show pursuits'}
              >
                {showPursuits ? 'Hide Pursuits' : 'Show Pursuits'}
              </button>
            ) : null}
          </div>

          {/* When the header is too narrow for the segment above, the same two actions live here. */}
          {canPullPursuits || hasAnyPursuit ? (
            <Dropdown
              className="ppc-projects-overflow"
              placement="bottomEnd"
              renderToggle={(props, ref) => (
                <button
                  {...props}
                  ref={ref}
                  type="button"
                  className="ppc-projects-btn ppc-projects-btn--icon"
                  aria-label="Pursuit actions"
                  title="Pursuit actions"
                >
                  <i className="bi bi-three-dots-vertical" aria-hidden="true" />
                </button>
              )}
            >
              {canPullPursuits ? (
                <Dropdown.Item
                  icon={<i className="bi bi-cloud-arrow-down" aria-hidden="true" />}
                  onSelect={() => {
                    setShowPullPursuitsModal(true);
                    void refetchPursuits();
                  }}
                >
                  Pull Pursuits
                </Dropdown.Item>
              ) : null}
              {hasAnyPursuit ? (
                <Dropdown.Item
                  icon={<i className={`bi ${showPursuits ? 'bi-eye-slash' : 'bi-eye'}`} aria-hidden="true" />}
                  onSelect={() => setShowPursuits((prev) => !prev)}
                >
                  {showPursuits ? 'Hide Pursuits' : 'Show Pursuits'}
                </Dropdown.Item>
              ) : null}
            </Dropdown>
          ) : null}

          {canCreateProjects(permissions) ? (
            <button
              type="button"
              className="ppc-projects-btn ppc-projects-btn--primary"
              onClick={() => navigate('/projects/new')}
              aria-label="New Project"
              title="New Project"
            >
              New<span className="ppc-projects-btn__long"> Project</span>
              <i className="bi bi-plus-lg" aria-hidden="true" />
            </button>
          ) : null}
        </div>
      </div>

      <Divider />

      <section>
        {projectsLoading ? <div>Loading projects...</div> : null}
        {projectsError ? <Message type="error" showIcon>Failed to load projects.</Message> : null}

        {projectView === 'list' ? (
          <div className="ppc-cfg-tablescope">
            <div className="nsi-table-wrap">
              <table className="nsi-table">
                <thead>
                  <tr>
                    <th scope="col" aria-sort={listSort.ariaSort('name')}>
                      <button type="button" className="nsi-table-sort" onClick={() => listSort.toggleSort('name')}>
                        Project
                        <i className={listSort.sortIconClass('name')} aria-hidden="true" />
                      </button>
                    </th>
                    <th scope="col" style={{ width: 190 }} aria-sort={listSort.ariaSort('startDate')}>
                      <button type="button" className="nsi-table-sort" onClick={() => listSort.toggleSort('startDate')}>
                        Dates
                        <i className={listSort.sortIconClass('startDate')} aria-hidden="true" />
                      </button>
                    </th>
                    <th scope="col" style={{ width: 180 }} aria-sort={listSort.ariaSort('location')}>
                      <button type="button" className="nsi-table-sort" onClick={() => listSort.toggleSort('location')}>
                        Location
                        <i className={listSort.sortIconClass('location')} aria-hidden="true" />
                      </button>
                    </th>
                    <th scope="col" style={{ width: 110 }} aria-sort={listSort.ariaSort('people')}>
                      <button type="button" className="nsi-table-sort" onClick={() => listSort.toggleSort('people')}>
                        People
                        <i className={listSort.sortIconClass('people')} aria-hidden="true" />
                      </button>
                    </th>
                    <th scope="col" className="nsi-table__right" style={{ width: 180 }} aria-sort={listSort.ariaSort('status')}>
                      <button type="button" className="nsi-table-sort" onClick={() => listSort.toggleSort('status')}>
                        Status
                        <i className={listSort.sortIconClass('status')} aria-hidden="true" />
                      </button>
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {projectsLoading ? (
                    <tr>
                      <td className="nsi-table__empty" colSpan={5}>Loading projects…</td>
                    </tr>
                  ) : null}

                  {!projectsLoading && sortedListRows.length === 0 ? (
                    <tr>
                      <td className="nsi-table__empty" colSpan={5}>No projects match your search.</td>
                    </tr>
                  ) : null}

                  {!projectsLoading && sortedListRows.map((row) => (
                    <tr
                      key={String(row.pid)}
                      className="nsi-table__row"
                      onClick={() => openProject(row.pid)}
                      onContextMenu={(e) => openProjectMenu(e, row.pid, row.name)}
                      onKeyDown={(e) => {
                        if (isContextMenuKey(e)) {
                          openProjectMenu(e, row.pid, row.name, contextMenuPointFor(e.currentTarget));
                          return;
                        }
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          openProject(row.pid);
                        }
                      }}
                      tabIndex={0}
                      role="button"
                    >
                      <td>
                        <span className="ppc-cfg-projectcell">
                          {row.photoUrl ? (
                            <img className="ppc-cfg-projectcell__logo" src={row.photoUrl} alt="" loading="lazy" />
                          ) : (
                            <span className="ppc-cfg-projectcell__logo ppc-cfg-projectcell__logo--empty" aria-hidden="true">
                              {initialForName(row.name)}
                            </span>
                          )}
                          <span className="ppc-cfg-projectcell__name">{row.name}</span>
                        </span>
                      </td>
                      <td className="nsi-table__num">{row.dateRange}</td>
                      <td className="nsi-table__muted">{row.locationLabel}</td>
                      <td className="nsi-table__num">
                        {row.peopleCount > 0 ? row.peopleCount : '—'}
                      </td>
                      <td className="nsi-table__right">
                        {row.pursuit ? (
                          <span className="ppc-projects-status ppc-projects-status--pursuit">Pursuit</span>
                        ) : row.remainingTime ? (
                          <span
                            className={`ppc-projects-status${
                              String(row.remainingTime).toLowerCase() === 'ended' ? ' ppc-projects-status--ended' : ''
                            }`}
                          >
                            {row.remainingTime}
                          </span>
                        ) : (
                          <span className="nsi-table__muted">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
        <div className="ppc-home-project-grid" role="list" aria-label="Projects">
          {gridProjects.map((p) => {
            const pid = Number(p?.id);
            const pursuit = isPursuitProject(p);
            const assignedPeopleCount = Number(p?.assignedPeopleCount ?? p?.assigned_people_count ?? 0);
            const startDate = p?.startDate ?? p?.start_date;
            const endDate = p?.endDate ?? p?.end_date;
            const locationCity = String(p?.locationCity ?? p?.location_city ?? '').trim();
            const locationLat = parseCoordinate(p?.locationLat ?? p?.location_lat);
            const locationLng = parseCoordinate(p?.locationLng ?? p?.location_lng);
            const hasCoordinates = hasNonZeroCoordinates(locationLat, locationLng);
            const locationLabel = locationCity || (hasCoordinates ? `${locationLat.toFixed(3)}, ${locationLng.toFixed(3)}` : 'Not set');
            const assignedPeopleNames = Array.isArray(p?.assignedPeopleNames) ? p.assignedPeopleNames : [];
            const rangeStart = formatMonthYear(startDate);
            const rangeEnd = formatMonthYear(endDate);
            const dateRange = rangeStart && rangeEnd ? `${rangeStart} - ${rangeEnd}` : '—';
            const remainingTime = formatRemainingTime(startDate, endDate);
            const visiblePeople = assignedPeopleNames.slice(0, 6);
            const overflowPeople = Math.max(0, assignedPeopleNames.length - visiblePeople.length);
            const hasLogo = Boolean(p?.photoUrl);

            return (
              <Panel
                key={String(pid)}
                bordered
                className={`ppc-home-project-card${pursuit ? ' ppc-home-project-card--pursuit' : ''}`}
                role="listitem"
                tabIndex={0}
                onClick={() => openProject(pid)}
                onContextMenu={(e) => openProjectMenu(e, pid, String(p?.name || ''))}
                onKeyDown={(e) => {
                  if (isContextMenuKey(e)) {
                    openProjectMenu(e, pid, String(p?.name || ''), contextMenuPointFor(e.currentTarget));
                    return;
                  }
                  if (e.key === 'Enter') openProject(pid);
                }}
              >
                <button
                  type="button"
                  className="ppc-home-project-card__menu"
                  aria-label={`Actions for ${p?.name || 'project'}`}
                  aria-haspopup="menu"
                  aria-expanded={Boolean(projectMenu?.anchor) && projectMenu?.pid === pid}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (projectMenu?.anchor === e.currentTarget) {
                      closeProjectMenu();
                      return;
                    }
                    const r = e.currentTarget.getBoundingClientRect();
                    openProjectMenu(e, pid, String(p?.name || ''), {
                      x: r.left,
                      y: r.bottom + 4,
                      anchor: e.currentTarget,
                      pid,
                    });
                  }}
                  // Enter/Space here work the button; keep them from also opening the project.
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  <i className="bi bi-three-dots-vertical" aria-hidden="true" />
                </button>

                {/* One corner chip per card. A pursuit says so instead of counting
                    down, since its dates are speculative. */}
                {pursuit ? (
                  <span className="ppc-project-card-remaining-badge ppc-project-card-remaining-badge--pursuit">
                    Pursuit
                  </span>
                ) : remainingTime ? (
                  <span
                    className={`ppc-project-card-remaining-badge${
                      String(remainingTime).toLowerCase() === 'ended' ? ' ppc-project-card-remaining-badge--ended' : ''
                    }`}
                  >
                    {remainingTime}
                  </span>
                ) : null}

                <div className="ppc-project-card-layout">
                  <div className={`ppc-project-card-top-row${hasLogo ? '' : ' ppc-project-card-top-row--no-logo'}`}>
                    <CardTitle text={p?.name || 'Untitled Project'} />

                    {hasLogo ? (
                      <div className="ppc-project-card-logo-wrap" aria-label="Project logo">
                        <img src={p.photoUrl} alt="Project logo" className="ppc-project-card-logo" />
                      </div>
                    ) : null}
                  </div>

                  <div className="ppc-project-card-range">{dateRange}</div>

                  <div className="ppc-project-card-location">Location: {locationLabel}</div>

                  <div className="ppc-project-card-people-section">
                    <div className="ppc-project-card-people-head">
                      <span className="ppc-home-project-card__label">People</span>
                      {Number.isFinite(assignedPeopleCount) && assignedPeopleCount > 0 ? (
                        <span className="ppc-project-card-people-bubble">{assignedPeopleCount}</span>
                      ) : null}
                    </div>
                    <div className="ppc-project-card-people-row">
                      {visiblePeople.length > 0 ? (
                        visiblePeople.map((name, idx) => (
                          <Whisper key={`${name}-${idx}`} placement="top" trigger="hover" speaker={<Tooltip>{name}</Tooltip>}>
                            <div className="ppc-home-avatar ppc-home-avatar--named" aria-label={name} style={avatarStyleForName(name)}>
                              {initialsForName(name)}
                            </div>
                          </Whisper>
                        ))
                      ) : (
                        <span className="ppc-project-card-people-empty">No assigned people</span>
                      )}
                      {overflowPeople > 0 ? (
                        <div className="ppc-home-avatar" title={`${overflowPeople} more`} aria-label={`${overflowPeople} more`}>
                          +{overflowPeople}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              </Panel>
            );
          })}
        </div>
        )}

        {projectView === 'grid' && !projectsLoading && !projectsError && filteredProjects.length === 0 ? (
          <Message type="info" showIcon style={{ marginTop: 12 }}>
            No projects match your search.
          </Message>
        ) : null}
      </section>

      {hidingEnabled && hiddenProjects.length > 0 ? (
        <Panel
          bordered
          collapsible
          expanded={hiddenExpanded}
          onSelect={() => setHiddenExpanded((prev) => !prev)}
          header={`Hidden (${hiddenProjects.length})`}
          style={{ marginTop: 24 }}
        >
          <div style={{ display: 'grid', gap: 8 }}>
            {hiddenProjects.map((p) => {
              const pid = Number(p?.id);
              const name = String(p?.name || 'Untitled Project');
              const rangeStart = formatMonthYear(p?.startDate ?? p?.start_date);
              const rangeEnd = formatMonthYear(p?.endDate ?? p?.end_date);

              return (
                <Panel
                  key={`hidden-${pid}`}
                  bordered
                  bodyFill
                  style={{ padding: 10 }}
                  onContextMenu={(e) => openProjectMenu(e, pid, name)}
                >
                  <Stack justifyContent="space-between" alignItems="center" wrap>
                    <div style={{ minWidth: 220 }}>
                      <Button
                        appearance="link"
                        size="sm"
                        style={{ padding: 0, fontWeight: 700 }}
                        onClick={() => openProject(pid)}
                      >
                        {name}
                      </Button>
                      <div style={{ fontSize: 12, color: '#666' }}>
                        {rangeStart && rangeEnd ? `${rangeStart} - ${rangeEnd}` : '—'}
                      </div>
                    </div>
                    <Button
                      appearance="ghost"
                      size="sm"
                      onClick={() => setProjectHidden.mutate({ projectId: pid, hidden: false })}
                    >
                      Unhide
                    </Button>
                  </Stack>
                </Panel>
              );
            })}
          </div>
        </Panel>
      ) : null}

      <section className="ppc-projects-section" style={{ marginTop: 24 }}>
        <div className="ppc-projects-section__head">
          <h3 className="ppc-projects-section__title">Project Map</h3>
          <span className="ppc-projects-count">{projectsWithLocation.length}</span>
        </div>

        {projectsWithLocation.length === 0 ? (
          <Message type="info" showIcon style={{ marginBottom: 12 }}>
            No project locations yet. Add a location (city and/or map pin) to projects to see markers.
          </Message>
        ) : null}

        <div className="ppc-projects-map-wrap">
          <div ref={mapContainerRef} style={{ width: '100%', height: '100%' }} />
        </div>
      </section>

      {/* Nothing archived means nothing to say — the section and its divider
          stay out of the page entirely rather than showing an empty panel. */}
      {archivedProjects.length > 0 || archivedProjectsError ? (
        <>
      <Divider />

      <Panel
        bordered
        collapsible
        expanded={archivedExpanded}
        onSelect={() => setArchivedExpanded((prev) => !prev)}
        header={`Archived Projects (${archivedProjects.length})`}
      >
        {archivedProjectsError ? <Message type="error" showIcon>Failed to load archived projects.</Message> : null}

        <div style={{ display: 'grid', gap: 8 }}>
          {archivedProjects.map((p) => {
            const pid = Number(p?.id);
            const startDate = p?.startDate ?? p?.start_date;
            const endDate = p?.endDate ?? p?.end_date;
            const rangeStart = formatMonthYear(startDate);
            const rangeEnd = formatMonthYear(endDate);
            const dateRange = rangeStart && rangeEnd ? `${rangeStart} - ${rangeEnd}` : '—';

            return (
              <Panel key={`archived-${pid}`} bordered bodyFill style={{ padding: 10 }}>
                <Stack justifyContent="space-between" alignItems="center" wrap>
                  <div style={{ minWidth: 220 }}>
                    <div style={{ fontWeight: 700 }}>{String(p?.name || 'Untitled Project')}</div>
                    <div style={{ fontSize: 12, color: '#666' }}>{dateRange}</div>
                  </div>

                  <Stack spacing={8}>
                    {permissions.canProject(pid, 'full') ? (
                      <Button
                        appearance="ghost"
                        color="red"
                        size="sm"
                        loading={trashProject.isPending}
                        disabled={!Number.isInteger(pid) || trashProject.isPending}
                        onClick={() => setPendingTrashProject({ id: pid, name: String(p?.name || 'this project') })}
                      >
                        Move to trash
                      </Button>
                    ) : null}
                    {permissions.canProject(pid, 'manage') ? (
                      <Button
                        appearance="ghost"
                        color="green"
                        size="sm"
                        loading={unarchiveProject.isPending}
                        disabled={!Number.isInteger(pid) || unarchiveProject.isPending}
                        onClick={() => {
                          if (!Number.isInteger(pid)) return;
                          void unarchiveProject.mutateAsync(pid);
                        }}
                      >
                        Unarchive
                      </Button>
                    ) : null}
                  </Stack>
                </Stack>
              </Panel>
            );
          })}
        </div>
      </Panel>
        </>
      ) : null}

      <Modal
        open={Boolean(pendingTrashProject)}
        onClose={() => {
          if (trashProject.isPending) return;
          setPendingTrashProject(null);
        }}
        size="xs"
      >
        <Modal.Header>
          <Modal.Title>Move project to trash?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Message type="warning" showIcon>
            <strong>{pendingTrashProjectShown?.name || 'This project'}</strong> will be moved to the trash.
          </Message>
          <div style={{ marginTop: 10, fontSize: 13, color: 'var(--rs-text-secondary)' }}>
            Trashed projects are permanently deleted after 30 days. You can restore it from
            Configuration → Project Management before then.
          </div>
        </Modal.Body>
        <Modal.Footer>
          <NsiButton
            onClick={() => setPendingTrashProject(null)}
            disabled={trashProject.isPending}
          >
            Cancel
          </NsiButton>
          <NsiButton
            variant="danger-solid"
            loading={trashProject.isPending}
            disabled={trashProject.isPending}
            onClick={() => void submitTrashProject()}
          >
            Move to trash
          </NsiButton>
        </Modal.Footer>
      </Modal>

      <Modal
        size="lg"
        open={showPullPursuitsModal && canPullPursuits}
        onClose={() => setShowPullPursuitsModal(false)}
      >
        <Modal.Header>
          <Modal.Title>Pull Pursuits</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="ppc-pursuits-modal-search">
            <Input
              value={pursuitSearch}
              onChange={setPursuitSearch}
              placeholder="Search pursuits by name or owner"
              aria-label="Search pursuits"
            />
          </div>

          {pursuitsLoading || pursuitsFetching ? (
            <Stack spacing={8} alignItems="center" style={{ padding: '8px 0' }}>
              <Loader content="Loading pursuits..." />
            </Stack>
          ) : null}

          {pursuitsError ? (
            <Message type="error" showIcon>
              Failed to load pursuits: {String(pursuitsError?.message || pursuitsError)}
            </Message>
          ) : null}

          {!pursuitsLoading && !pursuitsFetching && !pursuitsError && importablePursuits.length === 0 ? (
            <Message type="info" showIcon>
              No active pursuits available to import.
            </Message>
          ) : null}

          {!pursuitsLoading && !pursuitsFetching && !pursuitsError && importablePursuits.length > 0 && filteredImportablePursuits.length === 0 ? (
            <Message type="info" showIcon>
              No pursuits match your search.
            </Message>
          ) : null}

          <div className="ppc-pursuits-modal-list">
            {filteredImportablePursuits.map((row) => {
              const rowKey = String(row?.rowKey || row?.key || '');
              const done = row.alreadyExists || row.alreadyImported || row.alreadyLinked;
              const disabled = done || activeImportKey === rowKey;
              const canImport = !disabled && !createPursuitConnectProject.isPending;

              return (
                <div key={rowKey} className="ppc-pursuits-row">
                  <div className="ppc-pursuits-row__meta">
                    <div className="ppc-pursuits-row__grid">
                      <div className="ppc-pursuits-row__name">{row.name || 'Untitled pursuit'}</div>
                      <div className="ppc-pursuits-row__cell">{row.estimatedStartDate || '—'}</div>
                      <div className="ppc-pursuits-row__cell">{formatCurrency(row.totalBudget)}</div>
                      <div className="ppc-pursuits-row__cell">{row.pursuitOwner || '—'}</div>
                      <div className="ppc-pursuits-row__cell">{formatWinLikelihood(row.winLikelihood)}</div>
                    </div>

                    <div className="ppc-pursuits-row__suggested">
                      Suggested PIC: {row.suggestedPic?.name || 'No matching user found'}
                    </div>
                  </div>

                  <div className="ppc-pursuits-row__action">
                    <Button
                      size="xs"
                      appearance={done ? 'ghost' : 'primary'}
                      disabled={!canImport}
                      loading={activeImportKey === rowKey && createPursuitConnectProject.isPending}
                      onClick={() => {
                        void onImportPursuit(row);
                      }}
                    >
                      {row.alreadyImported
                        ? 'Imported'
                        : row.alreadyLinked
                          ? 'Already Linked'
                          : row.alreadyExists
                            ? 'Already Exists'
                            : 'Create Project'}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <NsiButton
            onClick={() => {
              void refetchPursuits();
            }}
            disabled={pursuitsLoading || pursuitsFetching}
          >
            Refresh
          </NsiButton>
          <NsiButton onClick={() => setShowPullPursuitsModal(false)}>
            Close
          </NsiButton>
        </Modal.Footer>
      </Modal>

      <ContextMenu menu={projectMenu} onClose={closeProjectMenu} label="Project actions" />

      <Modal
        open={Boolean(archiveTarget)}
        onClose={() => {
          if (archiveProject.isPending) return;
          setArchiveTarget(null);
        }}
        size="sm"
      >
        <Modal.Header>
          <Modal.Title>Archive this project?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Message type="warning" showIcon>
            <strong>{archiveTargetShown?.name || 'This project'}</strong> will be removed from the
            projects list, along with its task orders and forecasts.
          </Message>
          <div style={{ marginTop: 10 }}>
            Nothing is deleted. You can restore it from Archived Projects at the bottom of this page.
          </div>
          {archiveProject.isError ? (
            <Message type="error" showIcon style={{ marginTop: 10 }}>
              {String(archiveProject.error?.message || 'Failed to archive project.')}
            </Message>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <NsiButton onClick={() => setArchiveTarget(null)} disabled={archiveProject.isPending}>
            Cancel
          </NsiButton>
          <NsiButton
            variant="danger-solid"
            loading={archiveProject.isPending}
            disabled={archiveProject.isPending}
            onClick={() => void submitArchiveProject()}
          >
            Archive
          </NsiButton>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
